import React, { useState, useRef, useEffect } from 'react';
import { Recipe, Ingredient, AlternativeProduct, ShoppingList, User } from '../types';
import { generateAlternatives, generateRecipeImage, parseRecipeFromUrl, compressImage } from '../services/geminiService';
import { saveImageDocument, getImageDocument } from '../services/storageService';
import { ShareRecipeModal } from './ShareRecipeModal';

interface RecipeScreenProps {
  currentUser: User | null;
  recipes: Recipe[];
  shoppingLists: ShoppingList[];
  selectedRecipeId: string | null;
  onSelectRecipe: (id: string | null) => void;
  onBack: () => void;
  onAddIngredients: (ingredients: Ingredient[], targetListId?: string) => void;
  onAddRecipe: (recipe: Recipe) => void;
  onUpdateRecipe: (recipe: Recipe) => void;
  onDeleteRecipe: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onShowNotification?: (message: string) => void;
  onProcessImage: (base64: string) => void;
}

const BASE_CATEGORIES = ['Diner', 'Lunch', 'Ontbijt', 'Vega'];
const FILTERS = ['Alles', 'Favorieten', 'Snel', ...BASE_CATEGORIES];

export const RecipeScreen: React.FC<RecipeScreenProps> = ({ 
    currentUser,
    recipes,
    shoppingLists,
    selectedRecipeId,
    onSelectRecipe,
    onBack, 
    onAddIngredients, 
    onAddRecipe, 
    onUpdateRecipe,
    onDeleteRecipe,
    onToggleFavorite,
    onShowNotification,
    onProcessImage
}) => {
  // Main States
  const [portions, setPortions] = useState(4);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("Alles");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Selection Mode State (New)
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<Set<string>>(new Set());
  const isSelectionMode = selectedRecipeIds.size > 0;
  
  // Edit States
  const [isEditing, setIsEditing] = useState(false);
  const [editedRecipe, setEditedRecipe] = useState<Recipe | null>(null);
  const [isManualAdding, setIsManualAdding] = useState(false);
  const [manualTitle, setManualTitle] = useState("");
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Step Image Handling
  const [stepImagesData, setStepImagesData] = useState<Record<number, string>>({});
  const [loadingStepImages, setLoadingStepImages] = useState(false);
  const stepFileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // FAB State
  const [isFabOpen, setIsFabOpen] = useState(false);

  // Import states
  const [isImportingLink, setIsImportingLink] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [isScraping, setIsScraping] = useState(false);

  // Context Menu States (Ingredients)
  const [contextMenuIndex, setContextMenuIndex] = useState<number | null>(null);
  const [showQuickEditModal, setShowQuickEditModal] = useState(false);
  const [quickEditIngredient, setQuickEditIngredient] = useState<Ingredient>({ name: '', amount: '' });
  
  // AI States
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiAlternatives, setAiAlternatives] = useState<AlternativeProduct[]>([]);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [aiTargetIndex, setAiTargetIndex] = useState<number | null>(null);

  // UI States
  const [isSharing, setIsSharing] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  // Add Ingredients Flow
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [selectedTargetListId, setSelectedTargetListId] = useState<string>("");
  
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Selection Long Press Refs
  const recipeLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ignoreClickRef = useRef(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedRecipe = recipes.find(r => r.id === selectedRecipeId) || null;

  // Sync editedRecipe when entering edit mode
  useEffect(() => {
    if (isEditing && selectedRecipe) {
      setEditedRecipe(JSON.parse(JSON.stringify(selectedRecipe)));
    }
  }, [isEditing, selectedRecipe]);

  // Handle resets when switching recipes
  useEffect(() => {
    setIsEditing(false);
    setEditedRecipe(null);
    setContextMenuIndex(null);
    setPortions(4);
    setStepImagesData({});
    setIsGeneratingImage(false);
    setIsFabOpen(false); // Close FAB on nav
    setSelectedRecipeIds(new Set()); // Clear selection
  }, [selectedRecipeId]);

  // Set default target list
  useEffect(() => {
      if(shoppingLists.length > 0 && !selectedTargetListId) {
          setSelectedTargetListId(shoppingLists[0].id);
      }
  }, [shoppingLists]);

  // Start Camera Stream Logic
  useEffect(() => {
    const startStream = async () => {
      if (isScanning && videoRef.current) {
         try {
            // Probeer eerst high res, fallback naar environment default
            let stream;
            try {
               stream = await navigator.mediaDevices.getUserMedia({ 
                   video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } 
               });
            } catch (e) {
               stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            }
            
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.setAttribute("playsinline", "true"); 
                videoRef.current.play().catch(console.error);
            }
         } catch (err) {
            console.error("Camera start failed", err);
            if(onShowNotification) onShowNotification("Kon camera niet starten");
            setIsScanning(false);
         }
      }
    };

    if (isScanning) {
        startStream();
    } else {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    }
    
    return () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    };
  }, [isScanning]);

  // Load Step Images when viewing a recipe
  useEffect(() => {
      const loadImages = async () => {
          if (!selectedRecipe || !selectedRecipe.stepImageIds) return;
          
          setLoadingStepImages(true);
          const loaded: Record<number, string> = {};
          
          const promises = Object.entries(selectedRecipe.stepImageIds).map(async ([stepIdx, imageId]) => {
              const base64 = await getImageDocument(currentUser, String(imageId));
              if (base64) {
                  loaded[Number(stepIdx)] = base64;
              }
          });

          await Promise.all(promises);
          setStepImagesData(loaded);
          setLoadingStepImages(false);
      };

      if (selectedRecipeId && !isEditing) {
          loadImages();
      }
  }, [selectedRecipeId, currentUser, isEditing]);

  // --- Helpers ---
  const autoResizeTextarea = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = element.scrollHeight + 'px';
  };

  // --- Recipe Selection Logic ---
  const handleRecipeTouchStart = (id: string) => {
      if (isSelectionMode) return;
      recipeLongPressTimer.current = setTimeout(() => {
          if (navigator.vibrate) navigator.vibrate(50);
          const newSet = new Set(selectedRecipeIds);
          newSet.add(id);
          setSelectedRecipeIds(newSet);
          ignoreClickRef.current = true;
      }, 500);
  };

  const handleRecipeTouchEnd = () => {
      if (recipeLongPressTimer.current) {
          clearTimeout(recipeLongPressTimer.current);
      }
      setTimeout(() => { ignoreClickRef.current = false; }, 100);
  };

  const handleRecipeClick = (id: string) => {
      if (ignoreClickRef.current) return; // Was long press
      
      if (isSelectionMode) {
          const newSet = new Set(selectedRecipeIds);
          if (newSet.has(id)) {
              newSet.delete(id);
              // If last item unselected, exit mode
              if (newSet.size === 0) {
                 setSelectedRecipeIds(newSet);
              } else {
                 setSelectedRecipeIds(newSet);
              }
          } else {
              newSet.add(id);
              setSelectedRecipeIds(newSet);
          }
      } else {
          onSelectRecipe(id);
      }
  };

  const handleBatchDelete = () => {
      if (window.confirm(`Weet je zeker dat je ${selectedRecipeIds.size} recepten wilt verwijderen?`)) {
          selectedRecipeIds.forEach(id => onDeleteRecipe(id));
          setSelectedRecipeIds(new Set());
          if(onShowNotification) onShowNotification("Recepten verwijderd");
      }
  };

  const handleShareRecipe = () => {
      if (!selectedRecipe) return;
      setIsSharing(true);
  };

  // --- Ingredient Long Press Logic ---
  const handleTouchStart = (idx: number) => {
    if (isEditing) return;
    timerRef.current = setTimeout(() => {
      setContextMenuIndex(idx);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 500);
  };

  const handleTouchEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  // --- Actions ---
  const handleSaveFullEdit = () => {
    if (editedRecipe) {
      onUpdateRecipe(editedRecipe);
      setIsEditing(false);
      if (onShowNotification) onShowNotification("Recept opgeslagen");
    }
  };

  const handleGenerateImage = async () => {
    if (!editedRecipe) return;
    if (!currentUser) {
        if(onShowNotification) onShowNotification("Log in om AI afbeeldingen te genereren.");
        return;
    }
    
    setIsGeneratingImage(true);
    if(onShowNotification) onShowNotification("AI genereert een foto op basis van de titel...");
    
    try {
        const newImage = await generateRecipeImage(editedRecipe.title);
        if (newImage) {
            setEditedRecipe(prev => prev ? { ...prev, image: newImage } : null);
            if(onShowNotification) onShowNotification("Foto bijgewerkt!");
        } else {
            if(onShowNotification) onShowNotification("Kon geen foto genereren.");
        }
    } catch (e) {
        console.error(e);
        if(onShowNotification) onShowNotification("Er ging iets mis.");
    } finally {
        setIsGeneratingImage(false);
    }
  };

  const handleQuickEditSave = () => {
    if (selectedRecipe && contextMenuIndex !== null) {
      const newIngs = [...selectedRecipe.ingredients];
      newIngs[contextMenuIndex] = quickEditIngredient;
      onUpdateRecipe({ ...selectedRecipe, ingredients: newIngs });
      setShowQuickEditModal(false);
      setContextMenuIndex(null);
      if (onShowNotification) onShowNotification("Ingrediënt bijgewerkt");
    }
  };

  const handleDeleteIngredient = (idx: number) => {
    if (selectedRecipe && window.confirm("Ingrediënt verwijderen?")) {
      const newIngs = selectedRecipe.ingredients.filter((_, i) => i !== idx);
      onUpdateRecipe({ ...selectedRecipe, ingredients: newIngs });
      setContextMenuIndex(null);
    }
  };

  const handleAiAlternativesRequest = async (idx: number) => {
    if (!currentUser) {
        if (onShowNotification) onShowNotification("Log in om AI te gebruiken");
        setContextMenuIndex(null);
        return;
    }
    const ing = selectedRecipe?.ingredients[idx];
    if (!ing) return;

    setAiTargetIndex(idx);
    setContextMenuIndex(null);
    setShowAiModal(true);
    setIsLoadingAi(true);
    setAiAlternatives([]);

    try {
      const results = await generateAlternatives(ing.name);
      setAiAlternatives(results);
    } catch (e) {
      if (onShowNotification) onShowNotification("Kon geen alternatieven vinden");
      setShowAiModal(false);
    } finally {
      setIsLoadingAi(false);
    }
  };

  const applyAiAlternative = (altName: string) => {
    if (selectedRecipe && aiTargetIndex !== null) {
      const newIngs = [...selectedRecipe.ingredients];
      newIngs[aiTargetIndex] = { ...newIngs[aiTargetIndex], name: altName };
      onUpdateRecipe({ ...selectedRecipe, ingredients: newIngs });
      setShowAiModal(false);
      if (onShowNotification) onShowNotification("Ingrediënt vervangen");
    }
  };

  const handleScanClick = () => {
      if (!currentUser) {
          if (onShowNotification) onShowNotification("Log in om te scannen");
          return;
      }
      setIsScanning(true);
  };

  const handleStepImageUpload = async (stepIndex: number, file: File) => {
      if (!editedRecipe) return;
      
      try {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = async (e) => {
              const rawBase64 = e.target?.result as string;
              if (rawBase64) {
                  const compressed = await compressImage(rawBase64);
                  if (onShowNotification) onShowNotification("Foto uploaden...");
                  const imageId = await saveImageDocument(currentUser, compressed);
                  
                  setEditedRecipe(prev => {
                      if (!prev) return null;
                      const newImageIds = { ...(prev.stepImageIds || {}) };
                      newImageIds[stepIndex] = imageId;
                      return { ...prev, stepImageIds: newImageIds };
                  });
                  setStepImagesData(prev => ({ ...prev, [stepIndex]: compressed }));
              }
          };
      } catch (err) {
          console.error(err);
          if (onShowNotification) onShowNotification("Fout bij uploaden foto");
      }
  };

  const removeStepImage = (stepIndex: number) => {
      setEditedRecipe(prev => {
          if (!prev) return null;
          const newImageIds = { ...(prev.stepImageIds || {}) };
          delete newImageIds[stepIndex];
          return { ...prev, stepImageIds: newImageIds };
      });
      setStepImagesData(prev => {
          const newState = { ...prev };
          delete newState[stepIndex];
          return newState;
      });
  };

  const handleImportLinkClick = () => {
      if (!currentUser) {
          if (onShowNotification) onShowNotification("Log in om links te importeren");
          return;
      }
      setIsImportingLink(true);
  };

  const handleImportLink = async () => {
    if (!importUrl.trim()) return;
    
    const targetUrl = importUrl.trim();
    const tempId = `scrape-${Date.now()}`;
    
    setIsImportingLink(false);
    setImportUrl("");
    
    const placeholder: Recipe = {
      id: tempId,
      title: "Recept inlezen...",
      image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80",
      time: "...",
      category: "Diner",
      ingredients: [],
      steps: [],
      isFavorite: false,
      status: 'processing'
    };
    onAddRecipe(placeholder);
    
    if (onShowNotification) onShowNotification("AI is de website op de achtergrond aan het lezen...");
    
    try {
      const newRecipe = await parseRecipeFromUrl(targetUrl);
      if (newRecipe) {
        onUpdateRecipe({ ...newRecipe, id: tempId });
        if (onShowNotification) onShowNotification("Import voltooid!");
      } else {
        throw new Error("Geen recept gevonden");
      }
    } catch (e) {
      onDeleteRecipe(tempId);
      if (onShowNotification) onShowNotification("Kon geen recept vinden op deze link.");
    }
  };

  const handleConfirmAddToList = (scaledIngredients: Ingredient[]) => {
      if (!selectedTargetListId) return;
      onAddIngredients(scaledIngredients, selectedTargetListId);
      setShowAddToListModal(false);
  };

  // --- Rendering Helpers ---
  if (isScanning) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex flex-col">
        <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            muted 
            className="w-full h-full object-cover" 
        />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute inset-0 flex flex-col justify-between p-6 bg-gradient-to-b from-black/50 via-transparent to-black/50">
          <button onClick={() => setIsScanning(false)} className="self-end bg-black/40 p-3 rounded-full text-white backdrop-blur-md hover:bg-white/20"><span className="material-symbols-outlined">close</span></button>
          
          <div className="flex flex-col items-center">
             <div className="bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full mb-8">
                 <p className="text-white text-xs font-bold flex items-center gap-2"><span className="material-symbols-outlined text-sm text-primary">auto_fix_high</span> Maak foto van recept</p>
             </div>
             <button onClick={() => {
                if (!videoRef.current || !canvasRef.current) return;
                const canvas = canvasRef.current;
                canvas.width = videoRef.current.videoWidth;
                canvas.height = videoRef.current.videoHeight;
                canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0);
                onProcessImage(canvas.toDataURL('image/jpeg', 0.85));
                setIsScanning(false);
            }} className="h-20 w-20 rounded-full border-4 border-white bg-white/20 p-1.5 active:scale-95 transition-transform">
                <div className="w-full h-full bg-white rounded-full"></div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // DETAIL VIEW
  if (selectedRecipe) {
    if (selectedRecipe.status === 'processing') {
      return (
        <div className="h-screen w-full flex flex-col items-center justify-center p-8 bg-background-light dark:bg-background-dark">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <h2 className="text-xl font-bold mb-2">Recept wordt verwerkt...</h2>
          <button onClick={onBack} className="mt-4 text-primary font-bold">Terug</button>
        </div>
      );
    }

    const currentData = isEditing && editedRecipe ? editedRecipe : selectedRecipe;
    const ratio = portions / 4;

    const displayIngredients = currentData.ingredients.map(ing => {
      if (isEditing) return ing;
      return {
        ...ing,
        amount: ing.amount ? ing.amount.replace(/(\d+(?:[.,]\d+)?)/g, m => {
          const v = parseFloat(m.replace(',', '.'));
          return isNaN(v) ? m : parseFloat((v * ratio).toFixed(1)).toString().replace('.', ',');
        }) : ''
      };
    });

    return (
      <div className="relative flex h-screen w-full flex-col overflow-y-auto no-scrollbar bg-background-light dark:bg-background-dark pb-24">
        {/* Header Photo & Actions */}
        <div className="relative h-[360px] w-full shrink-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 pt-8">
            <button onClick={() => isEditing ? setIsEditing(false) : onBack()} className="size-10 flex items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/30 transition">
              <span className="material-symbols-outlined">{isEditing ? 'close' : 'arrow_back'}</span>
            </button>
            <div className="flex gap-2">
              {!isEditing ? (
                <>
                  <button 
                    onClick={() => onToggleFavorite(selectedRecipe.id)} 
                    className={`size-10 flex items-center justify-center rounded-full backdrop-blur-md transition-all duration-300 ${selectedRecipe.isFavorite ? 'bg-white text-red-500 shadow-lg scale-105' : 'bg-black/20 text-white hover:bg-black/30'}`}
                  >
                    <span className={`material-symbols-outlined ${selectedRecipe.isFavorite ? 'filled' : ''}`}>favorite</span>
                  </button>
                  <button onClick={() => setIsEditing(true)} className="size-10 flex items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/30 transition">
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button onClick={handleShareRecipe} className="size-10 flex items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/30 transition">
                    <span className="material-symbols-outlined">share</span>
                  </button>
                  <button onClick={() => { if(window.confirm("Verwijderen?")) { onDeleteRecipe(selectedRecipe.id); onBack(); } }} className="size-10 flex items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md hover:bg-black/30 transition">
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </>
              ) : (
                <button 
                    onClick={handleSaveFullEdit} 
                    className="px-6 h-10 bg-primary text-black font-bold rounded-full shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
                >
                    <span className="material-symbols-outlined text-[20px]">save</span>
                    Opslaan
                </button>
              )}
            </div>
          </div>
          <img src={currentData.image} className="w-full h-full object-cover" />
          
          {/* AI Image Generation Overlay (Only in Edit Mode) */}
          {isEditing && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/20 backdrop-blur-[2px]">
                <button
                    onClick={handleGenerateImage}
                    disabled={isGeneratingImage}
                    className="flex items-center gap-2 px-4 py-3 bg-primary text-black font-bold rounded-xl shadow-lg hover:scale-105 transition-transform disabled:opacity-70 disabled:hover:scale-100"
                >
                    <span className={`material-symbols-outlined ${isGeneratingImage ? 'animate-spin' : 'filled'}`}>
                        {isGeneratingImage ? 'sync' : 'auto_awesome'}
                    </span>
                    {isGeneratingImage ? 'Genereren...' : 'Nieuwe AI Foto'}
                </button>
                <p className="text-white text-[10px] font-bold opacity-80 uppercase tracking-widest text-center px-4">Genereert foto o.b.v. titel</p>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-0 p-6 w-full z-20">
            {isEditing ? (
              <input value={editedRecipe?.title} onChange={e => setEditedRecipe(prev => prev ? {...prev, title: e.target.value} : null)} className="w-full bg-black/40 border border-white/20 rounded-xl p-2 text-white font-bold text-2xl backdrop-blur-sm focus:ring-2 focus:ring-primary" placeholder="Titel" />
            ) : (
                <>
                  {selectedRecipe.originalAuthor && (
                        <div className="flex items-center gap-2 mb-2">
                            <span className="bg-primary text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Auteur</span>
                            <span className="text-white text-sm font-medium">{selectedRecipe.originalAuthor}</span>
                        </div>
                  )}
                  <h1 className="text-3xl font-bold text-white leading-tight">{selectedRecipe.title}</h1>
                </>
            )}
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Portions */}
          {!isEditing && (
            <div className="flex items-center justify-between bg-surface-light dark:bg-surface-dark p-2 rounded-2xl border border-gray-100 dark:border-white/5">
              <button onClick={() => setPortions(p => Math.max(1, p-1))} className="size-12 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition"><span className="material-symbols-outlined">remove</span></button>
              <div className="text-center"><p className="font-bold text-lg">{portions}</p><p className="text-[10px] text-gray-400 uppercase">Personen</p></div>
              <button onClick={() => setPortions(p => p+1)} className="size-12 rounded-xl bg-primary text-black hover:bg-green-400 transition"><span className="material-symbols-outlined">add</span></button>
            </div>
          )}

          {/* Ingredients */}
          <div>
            <h2 className="text-xl font-bold mb-4">Ingrediënten</h2>
            <div className="space-y-3">
              {displayIngredients.map((ing, idx) => (
                <div 
                  key={idx} 
                  onTouchStart={() => handleTouchStart(idx)}
                  onTouchEnd={handleTouchEnd}
                  onContextMenu={(e) => { e.preventDefault(); if(!isEditing) setContextMenuIndex(idx); }}
                  className={`transition-all select-none ${isEditing ? '' : 'p-4 rounded-xl flex items-center justify-between bg-surface-light dark:bg-surface-dark active:scale-[0.98]'}`}
                >
                  {isEditing ? (
                    <div className="flex w-full items-center gap-2 mb-2">
                        {/* Name Input */}
                        <div className="flex-1 bg-white dark:bg-black/20 rounded-xl border border-gray-200 dark:border-white/10 flex items-center px-3 py-3 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent transition-all">
                             <input 
                                value={ing.name} 
                                onChange={e => {
                                    const val = e.target.value;
                                    setEditedRecipe(prev => {
                                        if(!prev) return null;
                                        const newIngs = [...prev.ingredients];
                                        newIngs[idx] = { ...newIngs[idx], name: val };
                                        return { ...prev, ingredients: newIngs };
                                    });
                                }} 
                                placeholder="Naam (bv. Bloem)"
                                className="w-full bg-transparent border-none p-0 text-sm font-bold focus:ring-0 placeholder-gray-400"
                              />
                        </div>
                        
                        {/* Amount Input */}
                        <div className="w-24 bg-white dark:bg-black/20 rounded-xl border border-gray-200 dark:border-white/10 flex items-center px-3 py-3 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent transition-all">
                             <input 
                                value={ing.amount} 
                                onChange={e => {
                                    const val = e.target.value;
                                    setEditedRecipe(prev => {
                                        if(!prev) return null;
                                        const newIngs = [...prev.ingredients];
                                        newIngs[idx] = { ...newIngs[idx], amount: val };
                                        return { ...prev, ingredients: newIngs };
                                    });
                                }} 
                                placeholder="Aantal"
                                className="w-full bg-transparent border-none p-0 text-sm text-center font-medium focus:ring-0 placeholder-gray-400"
                              />
                        </div>

                        <button onClick={() => setEditedRecipe(p => p ? {...p, ingredients: p.ingredients.filter((_, i) => i !== idx)} : null)} className="size-11 flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-500 rounded-xl hover:bg-red-100 transition">
                            <span className="material-symbols-outlined">delete</span>
                        </button>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium text-gray-700 dark:text-gray-200">{ing.name}</span>
                      <span className="font-bold text-primary">{ing.amount}</span>
                    </>
                  )}
                </div>
              ))}
              {isEditing && (
                <button onClick={() => setEditedRecipe(p => p ? {...p, ingredients: [...p.ingredients, {name:'', amount:''}]} : null)} className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl text-gray-400 font-bold text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition">+ Ingrediënt toevoegen</button>
              )}
            </div>
          </div>

          {/* Steps */}
          <div>
            <h2 className="text-xl font-bold mb-4">Bereiding</h2>
            <div className="space-y-6">
              {currentData.steps.map((s, idx) => (
                <div key={idx} className={isEditing ? "w-full" : "flex gap-4"}>
                  {!isEditing && (
                      <div className="flex flex-col items-center gap-2">
                          <div className="size-8 shrink-0 bg-primary/20 text-primary rounded-full flex items-center justify-center font-bold text-sm">{idx+1}</div>
                          {idx < currentData.steps.length - 1 && <div className="w-0.5 flex-1 bg-gray-100 dark:bg-white/10 min-h-[20px]"></div>}
                      </div>
                  )}
                  
                  <div className={`flex-1 ${isEditing ? 'mb-4' : 'pb-4'}`}>
                    {isEditing ? (
                        <div className="bg-white dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-2xl p-4 shadow-sm relative group focus-within:ring-2 focus-within:ring-primary/50 transition-all">
                           <div className="flex justify-between items-start mb-3">
                               <span className="flex items-center justify-center size-6 bg-gray-100 dark:bg-white/10 rounded-full text-xs font-bold text-gray-500">{idx + 1}</span>
                               <button onClick={() => setEditedRecipe(p => p ? {...p, steps: p.steps.filter((_, i) => i !== idx)} : null)} className="text-gray-300 hover:text-red-500 transition-colors">
                                   <span className="material-symbols-outlined">close</span>
                               </button>
                           </div>

                           <textarea 
                                value={s} 
                                ref={(el) => { if(el) autoResizeTextarea(el); }}
                                onInput={(e) => autoResizeTextarea(e.target as HTMLTextAreaElement)}
                                onChange={e => {
                                      const val = e.target.value;
                                      setEditedRecipe(prev => {
                                          if (!prev) return null;
                                          const newSteps = [...prev.steps];
                                          newSteps[idx] = val;
                                          return { ...prev, steps: newSteps };
                                      });
                                }} 
                                placeholder={`Beschrijf stap ${idx + 1}...`}
                                className="w-full bg-transparent border-none p-0 text-sm min-h-[60px] resize-none focus:ring-0 leading-relaxed" 
                           />
                           
                           {/* Step Image UI */}
                           <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                                {(stepImagesData[idx] || (editedRecipe?.stepImageIds && editedRecipe.stepImageIds[idx])) ? (
                                    <div className="relative rounded-xl overflow-hidden aspect-video group/image">
                                        <img src={stepImagesData[idx]} className="w-full h-full object-cover" alt={`Stap ${idx + 1}`} />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button onClick={() => stepFileInputRefs.current[idx]?.click()} className="p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40"><span className="material-symbols-outlined">edit</span></button>
                                            <button onClick={() => removeStepImage(idx)} className="p-2 bg-red-500/80 backdrop-blur-md rounded-full text-white hover:bg-red-600"><span className="material-symbols-outlined">delete</span></button>
                                        </div>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => stepFileInputRefs.current[idx]?.click()}
                                        className="w-full py-3 border border-dashed border-gray-300 dark:border-white/20 rounded-xl text-gray-400 hover:text-primary hover:border-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2 text-xs font-bold"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">add_photo_alternate</span>
                                        Foto toevoegen
                                    </button>
                                )}
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    accept="image/*"
                                    ref={(el) => { stepFileInputRefs.current[idx] = el; }}
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) handleStepImageUpload(idx, e.target.files[0]);
                                    }}
                                />
                           </div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed pt-1">{s}</p>
                            {/* View Mode Image */}
                            {loadingStepImages ? (
                                selectedRecipe.stepImageIds && selectedRecipe.stepImageIds[idx] && <div className="mt-3 w-full h-40 bg-gray-100 dark:bg-white/5 animate-pulse rounded-xl"></div>
                            ) : (
                                stepImagesData[idx] && (
                                    <div className="mt-3 w-full rounded-2xl overflow-hidden shadow-sm border border-gray-100 dark:border-white/5">
                                        <img src={stepImagesData[idx]} className="w-full max-h-[300px] object-cover" alt={`Stap ${idx + 1}`} />
                                    </div>
                                )
                            )}
                        </div>
                    )}
                  </div>
                </div>
              ))}
              {isEditing && (
                <button onClick={() => setEditedRecipe(p => p ? {...p, steps: [...p.steps, '']} : null)} className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-xl text-gray-400 font-bold text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition">+ Stap toevoegen</button>
              )}
            </div>
          </div>
        </div>

        {/* Add to List FAB */}
        {!isEditing && (
            <button 
                onClick={() => setShowAddToListModal(true)}
                className="fixed bottom-6 right-6 z-40 h-16 w-16 rounded-full bg-accent-blue shadow-xl shadow-blue-500/40 flex items-center justify-center text-white active:scale-95 transition-transform hover:scale-105"
            >
                <span className="material-symbols-outlined text-3xl">add_shopping_cart</span>
            </button>
        )}

        {/* Bottom Menu (Context for Ingredient) */}
        {contextMenuIndex !== null && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center pointer-events-none p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto" onClick={() => setContextMenuIndex(null)} />
            <div className="w-full max-w-sm bg-white dark:bg-surface-dark rounded-[32px] p-6 shadow-2xl relative z-10 pointer-events-auto animate-in slide-in-from-bottom-10">
              <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-6" />
              <h3 className="text-lg font-bold mb-6 text-center">{selectedRecipe.ingredients[contextMenuIndex].name}</h3>
              <div className="space-y-3">
                <button onClick={() => { setQuickEditIngredient({...selectedRecipe.ingredients[contextMenuIndex]}); setShowQuickEditModal(true); setContextMenuIndex(null); }} className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-white/5 hover:bg-gray-100 transition">
                  <span className="material-symbols-outlined text-gray-500">edit</span><span className="font-bold text-sm">Snel aanpassen</span>
                </button>
                <button 
                    onClick={() => handleAiAlternativesRequest(contextMenuIndex)} 
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl bg-primary/10 hover:bg-primary/20 transition group ${!currentUser ? 'opacity-50' : ''}`}
                >
                  <span className="material-symbols-outlined text-primary filled">{currentUser ? 'auto_awesome' : 'lock'}</span>
                  <div className="text-left"><p className="font-bold text-sm text-primary">Alternatief met AI</p><p className="text-[10px] text-primary/70">{currentUser ? 'Vind gezondere of vega opties' : 'Login vereist'}</p></div>
                </button>
                <button onClick={() => handleDeleteIngredient(contextMenuIndex)} className="w-full flex items-center gap-4 p-4 rounded-2xl bg-red-50 dark:bg-red-900/10 text-red-500 hover:bg-red-100 transition">
                  <span className="material-symbols-outlined">delete</span><span className="font-bold text-sm">Verwijder</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add To List Modal */}
        {showAddToListModal && (
            <div className="fixed inset-0 z-[70] flex items-end justify-center p-4">
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAddToListModal(false)} />
                <div className="bg-white dark:bg-surface-dark w-full max-w-sm rounded-t-[32px] p-6 relative z-10 shadow-2xl">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <p className="text-xs font-bold text-accent-blue uppercase mb-1">Toevoegen aan lijst</p>
                            <h3 className="text-xl font-bold">Welke lijst?</h3>
                        </div>
                        <button onClick={() => setShowAddToListModal(false)} className="text-gray-400"><span className="material-symbols-outlined">close</span></button>
                    </div>
                    
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto mb-6">
                        {shoppingLists.map(list => (
                            <button
                                key={list.id}
                                onClick={() => setSelectedTargetListId(list.id)}
                                className={`w-full flex items-center p-4 rounded-2xl border transition-all ${selectedTargetListId === list.id ? 'bg-accent-blue/10 border-accent-blue' : 'bg-gray-50 dark:bg-white/5 border-transparent'}`}
                            >
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 ${selectedTargetListId === list.id ? 'border-accent-blue' : 'border-gray-300'}`}>
                                    {selectedTargetListId === list.id && <div className="w-3 h-3 rounded-full bg-accent-blue"></div>}
                                </div>
                                <div className="text-left flex-1">
                                    <span className="block font-bold text-sm">{list.name}</span>
                                    <span className="text-[10px] text-gray-500">{list.items.filter(i => !i.checked).length} items</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    <button 
                        onClick={() => handleConfirmAddToList(displayIngredients)}
                        className="w-full py-4 bg-accent-blue text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 active:scale-95 transition-transform"
                    >
                        {displayIngredients.length} Ingrediënten Toevoegen
                    </button>
                </div>
            </div>
        )}

        {/* Quick Edit Modal */}
        {showQuickEditModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowQuickEditModal(false)} />
            <div className="bg-white dark:bg-surface-dark w-full max-w-sm rounded-[32px] p-8 relative z-10 shadow-2xl">
              <h3 className="text-xl font-bold mb-6">Ingrediënt aanpassen</h3>
              <div className="space-y-4">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1 mb-1 block">Naam</label>
                    <input autoFocus value={quickEditIngredient.name} onChange={e => setQuickEditIngredient(p => ({...p, name: e.target.value}))} className="w-full bg-gray-50 dark:bg-black/20 rounded-xl px-4 py-3 font-bold border border-gray-200 dark:border-white/10" />
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1 mb-1 block">Hoeveelheid</label>
                    <input value={quickEditIngredient.amount} onChange={e => setQuickEditIngredient(p => ({...p, amount: e.target.value}))} className="w-full bg-gray-50 dark:bg-black/20 rounded-xl px-4 py-3 border border-gray-200 dark:border-white/10" placeholder="Bijv. 500g" />
                </div>
                <button onClick={handleQuickEditSave} className="w-full py-4 bg-primary text-black font-bold rounded-xl shadow-lg mt-2">Opslaan</button>
              </div>
            </div>
          </div>
        )}

        {/* AI Modal */}
        {showAiModal && (
          <div className="fixed inset-0 z-[70] flex items-end justify-center p-4">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAiModal(false)} />
            <div className="bg-white dark:bg-surface-dark w-full max-w-sm rounded-t-[32px] p-6 relative z-10 shadow-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div><p className="text-xs font-bold text-primary uppercase mb-1">Suggesties</p><h3 className="text-xl font-bold">Vervang door...</h3></div>
                <button onClick={() => setShowAiModal(false)} className="text-gray-400"><span className="material-symbols-outlined">close</span></button>
              </div>
              {isLoadingAi ? (
                <div className="py-12 flex flex-col items-center gap-4"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /><p className="text-sm text-gray-500">Alternatieven laden...</p></div>
              ) : (
                <div className="space-y-3">
                  {aiAlternatives.map((alt, i) => (
                    <div key={i} className="p-4 bg-surface-light dark:bg-black/20 rounded-2xl border border-gray-100 dark:border-white/5">
                      <div className="flex justify-between items-start gap-4 mb-2">
                        <div className="flex-1"><p className="font-bold text-base">{alt.name}</p><p className="text-xs text-gray-500 line-clamp-2">{alt.description}</p></div>
                        <button onClick={() => applyAiAlternative(alt.name)} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg text-xs font-bold">Kies</button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {alt.tags.map((t, ti) => <span key={ti} className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold ${t.colorClass}`}><span className="material-symbols-outlined text-[14px] mr-1">{t.icon}</span>{t.label}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <ShareRecipeModal
          isOpen={isSharing}
          onClose={() => setIsSharing(false)}
          recipe={selectedRecipe}
          currentUser={currentUser}
        />
      </div>
    );
  }

  // LIST VIEW (Recipes)
  const filteredRecipes = recipes.filter(r => {
      const matchesSearch = r.title.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      
      if (activeFilter === 'Alles') return true;
      if (activeFilter === 'Favorieten') return r.isFavorite;
      if (activeFilter === 'Snel') {
         const timeStr = r.time.toLowerCase();
         const minutes = parseInt(timeStr) || 30; 
         return minutes <= 30 && !timeStr.includes('uur');
      }
      return r.category === activeFilter;
  });

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark overflow-y-auto no-scrollbar pb-32">
      <header className={`sticky top-0 z-50 backdrop-blur-sm px-4 pt-6 pb-2 border-b transition-colors duration-300 ${isSelectionMode ? 'bg-primary text-black border-primary' : 'bg-background-light/95 dark:bg-background-dark/95 border-gray-100 dark:border-white/5'}`}>
        <div className="flex items-center justify-between">
          {isSelectionMode ? (
              <>
                <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedRecipeIds(new Set())} className="p-1 rounded-full hover:bg-black/10">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                    <span className="font-bold text-lg">{selectedRecipeIds.size} geselecteerd</span>
                </div>
                <button onClick={handleBatchDelete} className="p-2 rounded-full hover:bg-black/10 text-red-600 font-bold bg-white/20">
                    <span className="material-symbols-outlined">delete</span>
                </button>
              </>
          ) : (
              <>
                <div><h1 className="tracking-tight text-xl font-bold">Mijn Recepten</h1><p className="text-[10px] text-gray-400">{recipes.length} recepten</p></div>
                <div className="flex bg-surface-light dark:bg-white/5 p-1 rounded-lg">
                    <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-white/10 shadow text-primary' : 'text-gray-400'}`}>
                        <span className="material-symbols-outlined text-[20px]">grid_view</span>
                    </button>
                    <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white dark:bg-white/10 shadow text-primary' : 'text-gray-400'}`}>
                        <span className="material-symbols-outlined text-[20px]">view_list</span>
                    </button>
                </div>
              </>
          )}
        </div>
      </header>

      <section className="p-4 space-y-4">
        {!isSelectionMode && (
            <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-2.5 text-gray-400">search</span>
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Zoeken..." className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-surface-light dark:bg-white/5 border-none text-sm focus:ring-1 focus:ring-primary" />
            </div>
        )}

        {!isSelectionMode && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {FILTERS.map(f => (
                <button key={f} onClick={() => setActiveFilter(f)} className={`px-4 py-2 rounded-full text-[11px] font-bold whitespace-nowrap transition-colors ${activeFilter === f ? 'bg-primary text-black' : 'bg-surface-light dark:bg-white/5 text-gray-500'}`}>
                    {f === 'Favorieten' && <span className="material-symbols-outlined text-[14px] align-text-bottom mr-1">favorite</span>}
                    {f === 'Snel' && <span className="material-symbols-outlined text-[14px] align-text-bottom mr-1">timer</span>}
                    {f}
                </button>
            ))}
            </div>
        )}

        <div className={`grid gap-3 pb-20 ${viewMode === 'grid' ? 'grid-cols-3' : 'grid-cols-1'}`}>
          {filteredRecipes.map(r => {
            const isSelected = selectedRecipeIds.has(r.id);
            return (
                <div 
                    key={r.id} 
                    onContextMenu={(e) => { e.preventDefault(); }}
                    onTouchStart={() => handleRecipeTouchStart(r.id)}
                    onTouchEnd={handleRecipeTouchEnd}
                    onMouseDown={() => handleRecipeTouchStart(r.id)} // For desktop testing
                    onMouseUp={handleRecipeTouchEnd}
                    onClick={() => handleRecipeClick(r.id)} 
                    className={`cursor-pointer group relative transition-all duration-200 select-none touch-callout-none ${
                        viewMode === 'list' 
                            ? `flex gap-4 items-center p-2 rounded-xl border ${isSelected ? 'bg-primary/10 border-primary' : 'bg-surface-light dark:bg-white/5 border-transparent'}` 
                            : `flex flex-col gap-2`
                    } ${isSelected && viewMode === 'grid' ? 'scale-95' : ''}`}
                    style={{ WebkitTouchCallout: 'none' }}
                >
                    <div className={`rounded-xl overflow-hidden bg-gray-200 relative shadow-sm shrink-0 transition-all ${
                        viewMode === 'list' 
                            ? 'size-20' 
                            : `aspect-[4/5] w-full ${isSelected ? 'ring-4 ring-primary' : ''}`
                    }`}>
                        <img src={r.image} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none ${r.status === 'processing' ? 'opacity-50 animate-pulse' : ''}`} />
                        
                        {/* Grid Overlays */}
                        {viewMode === 'grid' && !isSelectionMode && (
                            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-[8px] text-white font-bold">{r.category}</div>
                        )}
                        
                        {/* Selection Overlay */}
                        {isSelected && (
                            <div className="absolute inset-0 bg-primary/30 flex items-center justify-center backdrop-blur-[1px]">
                                <div className="bg-white rounded-full p-1 shadow-md">
                                    <span className="material-symbols-outlined text-primary font-bold">check</span>
                                </div>
                            </div>
                        )}

                        {/* Favorite Button (Hidden in selection mode) */}
                        {!isSelectionMode && (
                            <button 
                                onClick={(e) => { e.stopPropagation(); onToggleFavorite(r.id); }}
                                className={`absolute top-2 right-2 h-8 w-8 flex items-center justify-center rounded-full backdrop-blur-md transition-all duration-300 shadow-sm ${
                                    r.isFavorite 
                                    ? 'bg-white text-red-500 scale-110' 
                                    : 'bg-black/20 text-white hover:bg-white/40'
                                }`}
                            >
                                <span className={`material-symbols-outlined text-[18px] ${r.isFavorite ? 'filled' : ''}`}>favorite</span>
                            </button>
                        )}
                    </div>
                
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <h3 className={`font-bold text-gray-900 dark:text-white line-clamp-2 ${viewMode === 'list' ? 'text-sm' : 'text-xs px-1'}`}>{r.title}</h3>
                        {viewMode === 'list' && (
                            <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-1">
                                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[12px]">schedule</span>{r.time}</span>
                                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[12px]">restaurant</span>{r.category}</span>
                            </div>
                        )}
                        {viewMode === 'grid' && !isSelectionMode && (
                            <div className="flex items-center gap-1 px-1 text-[10px] text-gray-400">
                                <span className="material-symbols-outlined text-[10px]">schedule</span> {r.time}
                            </div>
                        )}
                    </div>
                    {viewMode === 'list' && !isSelectionMode && <span className="material-symbols-outlined text-gray-300 ml-auto">chevron_right</span>}
                    {viewMode === 'list' && isSelected && <span className="material-symbols-outlined text-primary ml-auto filled">check_circle</span>}
                </div>
            );
          })}
        </div>
      </section>

      {/* Horizontal Expanding FAB (Hidden in selection mode) */}
      {!isSelectionMode && (
          <div className="fixed bottom-24 right-4 z-40 flex items-center justify-end">
              <div className={`flex items-center bg-accent-blue rounded-full shadow-lg transition-all duration-300 overflow-hidden ${isFabOpen ? 'w-auto pr-1' : 'w-14'}`}>
                  
                  {/* Expandable Options */}
                  <div className={`flex items-center gap-1 overflow-hidden transition-all duration-300 ${isFabOpen ? 'w-64 opacity-100 pl-2' : 'w-0 opacity-0'}`}>
                      <button onClick={handleScanClick} className="flex flex-col items-center justify-center w-14 h-12 rounded-full text-white hover:bg-white/20 active:scale-90 transition">
                          <span className="material-symbols-outlined text-[20px]">photo_camera</span>
                          <span className="text-[9px] font-bold">Camera</span>
                      </button>
                      <button onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center justify-center w-14 h-12 rounded-full text-white hover:bg-white/20 active:scale-90 transition">
                          <span className="material-symbols-outlined text-[20px]">upload_file</span>
                          <span className="text-[9px] font-bold">Upload</span>
                      </button>
                      <button onClick={() => setIsManualAdding(true)} className="flex flex-col items-center justify-center w-14 h-12 rounded-full text-white hover:bg-white/20 active:scale-90 transition">
                          <span className="material-symbols-outlined text-[20px]">edit</span>
                          <span className="text-[9px] font-bold">Typen</span>
                      </button>
                      <button onClick={handleImportLinkClick} className="flex flex-col items-center justify-center w-14 h-12 rounded-full text-white hover:bg-white/20 active:scale-90 transition">
                          <span className="material-symbols-outlined text-[20px]">link</span>
                          <span className="text-[9px] font-bold">Link</span>
                      </button>
                  </div>

                  {/* Main Toggle Button */}
                  <button 
                    onClick={() => setIsFabOpen(!isFabOpen)} 
                    className={`h-14 w-14 rounded-full flex items-center justify-center text-white shrink-0 hover:scale-105 active:scale-95 transition-transform`}
                  >
                      <span className={`material-symbols-outlined text-3xl transition-transform duration-300 ${isFabOpen ? 'rotate-45' : ''}`}>add</span>
                  </button>
              </div>
          </div>
      )}

      {/* Hidden File Input for Upload */}
      <input type="file" ref={fileInputRef} className="hidden" onChange={e => {
            const f = e.target.files?.[0];
            if(f){
              const r = new FileReader();
              r.onload = ev => onProcessImage(ev.target?.result as string);
              r.readAsDataURL(f);
            }
      }} />

      {/* Manual Add Modal */}
      {isManualAdding && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-24">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsManualAdding(false)} />
          <div className="bg-white dark:bg-surface-dark w-full max-w-sm rounded-2xl p-6 relative z-10 space-y-4">
            <h3 className="text-lg font-bold">Nieuw Recept</h3>
            <input autoFocus placeholder="Titel" value={manualTitle} onChange={e => setManualTitle(e.target.value)} className="w-full p-4 rounded-xl bg-gray-50 dark:bg-black/20 border-none font-bold outline-none focus:ring-1 focus:ring-primary" />
            <button onClick={() => {
              if(!manualTitle) return;
              const newR: Recipe = {
                id: Date.now().toString(),
                title: manualTitle,
                image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80",
                time: "30 min",
                category: "Diner",
                ingredients: [],
                steps: [],
                isFavorite: false,
                status: 'ready'
              };
              onAddRecipe(newR);
              setIsManualAdding(false);
              setManualTitle("");
              onSelectRecipe(newR.id);
            }} className="w-full py-4 bg-primary text-black font-bold rounded-xl shadow-lg">Starten</button>
          </div>
        </div>
      )}

      {/* URL Import Modal */}
      {isImportingLink && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !isScraping && setIsImportingLink(false)} />
          <div className="bg-white dark:bg-surface-dark w-full max-w-sm rounded-2xl p-6 relative z-10 space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold flex items-center gap-2"><span className="material-symbols-outlined text-accent-blue">link</span> Importeer via Link</h3>
            <p className="text-xs text-gray-500 leading-relaxed">Plak de URL van een recepten-website. Onze AI zal de ingrediënten en stappen automatisch voor je opzoeken en extraheren.</p>
            <input 
              autoFocus 
              disabled={isScraping}
              placeholder="https://..." 
              value={importUrl} 
              onChange={e => setImportUrl(e.target.value)} 
              className="w-full p-4 rounded-xl bg-gray-50 dark:bg-black/20 border-none font-medium outline-none focus:ring-1 focus:ring-accent-blue disabled:opacity-50" 
            />
            <div className="flex gap-2">
               <button onClick={() => setIsImportingLink(false)} disabled={isScraping} className="flex-1 py-3 bg-gray-100 dark:bg-white/10 rounded-xl font-bold text-sm">Annuleren</button>
               <button 
                onClick={handleImportLink} 
                disabled={isScraping || !importUrl.trim()} 
                className="flex-1 py-3 bg-accent-blue text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isScraping ? <span className="material-symbols-outlined animate-spin text-[18px]">sync</span> : 'Importeren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};